/**
 * Minimal AWS DynamoDB client for the browser (and Node tests), signed with Signature
 * Version 4 over the Web Crypto API (SubtleCrypto) — no AWS SDK dependency, matching this
 * codebase's hand-rolled-over-fetch style (cf. lib/geocoding.ts).
 *
 * Region/table come from the repo-root `aws.json` (bundled via resolveJsonModule — non-secret).
 * The credentials are NOT bundled: they are fetched from the Access API at startup (access_api.ts
 * / secrets_boot.ts) and installed via `set_aws_credentials`. Until then `dynamo_configured()` is
 * false and the app stays local-only.
 *
 * Data model: one DynamoDB item per (user, type). Partition key `user` = the profile slug
 * (the normalized first name); sort key `type` ∈ {base, run, times, finals}. The JSON payload is
 * stored verbatim as a single String attribute `data` (avoids DynamoDB attribute marshalling
 * of arbitrarily nested profile JSON, reserved-word collisions, and empty-string quirks).
 *
 * NOTE on CORS: AWS DynamoDB's JSON endpoint returns `Access-Control-Allow-Origin: *`, so a
 * browser `fetch` to it succeeds without a proxy. (Verified against the live `btr` table.)
 */

import awsConfig from "../../aws.json";

export interface AwsConfig {
  region: string;
  table: string;
}

const CFG = awsConfig as AwsConfig;
const SERVICE = "dynamodb";
const API_VERSION = "DynamoDB_20120810";

// Credentials are no longer bundled. They are fetched from the Access API at startup (see
// access_api.ts / secrets_boot.ts) and installed here via `set_aws_credentials`. Until then the
// app stays local-only (dynamo_configured() → false), exactly as it did with absent aws.json keys.
let _accessKeyId: string | null = null;
let _secretAccessKey: string | null = null;

/** Install (or clear, with null) the runtime AWS credentials fetched from the Access API. */
export function set_aws_credentials(creds: { accessKeyId: string; secretAccessKey: string } | null): void {
  if (creds === null) {
    _accessKeyId = null;
    _secretAccessKey = null;
    return;
  }
  _accessKeyId = creds.accessKeyId?.trim() || null;
  _secretAccessKey = creds.secretAccessKey?.trim() || null;
}

/** True when runtime credentials are installed (else the app stays local-only). */
export function dynamo_configured(): boolean {
  return Boolean(_accessKeyId && _secretAccessKey && CFG.region && CFG.table);
}

function endpoint(): string {
  return `https://${SERVICE}.${CFG.region}.amazonaws.com/`;
}

function host(): string {
  return `${SERVICE}.${CFG.region}.amazonaws.com`;
}

// ── Web Crypto helpers ─────────────────────────────────────────────────────────────────────

function _subtle(): SubtleCrypto {
  const c = (globalThis as any).crypto;
  if (!c?.subtle) throw new Error("Web Crypto (crypto.subtle) is unavailable in this environment");
  return c.subtle;
}

const _enc = new TextEncoder();

function toHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, "0");
  return out;
}

// TS 5.7 narrowed BufferSource to ArrayBuffer-backed views; TextEncoder/HMAC outputs are
// ArrayBufferLike-backed at the type level, so coerce at the Web Crypto boundary.
function buf(data: ArrayBuffer | Uint8Array): BufferSource {
  return data as unknown as BufferSource;
}

async function sha256Hex(message: string): Promise<string> {
  return toHex(await _subtle().digest("SHA-256", buf(_enc.encode(message))));
}

async function hmac(key: ArrayBuffer | Uint8Array, message: string): Promise<ArrayBuffer> {
  const cryptoKey = await _subtle().importKey("raw", buf(key), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return _subtle().sign("HMAC", cryptoKey, buf(_enc.encode(message)));
}

// ── SigV4 ───────────────────────────────────────────────────────────────────────────────────

function amzDate(now: Date): { amzdate: string; datestamp: string } {
  const iso = now.toISOString().replace(/[:-]|\.\d{3}/g, ""); // YYYYMMDDTHHMMSSZ
  return { amzdate: iso, datestamp: iso.slice(0, 8) };
}

async function signingKey(secret: string, datestamp: string, region: string, service: string): Promise<ArrayBuffer> {
  const kDate = await hmac(_enc.encode(`AWS4${secret}`), datestamp);
  const kRegion = await hmac(kDate, region);
  const kService = await hmac(kRegion, service);
  return hmac(kService, "aws4_request");
}

/** Build the SigV4 Authorization header for a DynamoDB POST with body `payload`. */
async function authHeaders(target: string, payload: string): Promise<Record<string, string>> {
  const now = new Date();
  const { amzdate, datestamp } = amzDate(now);
  const h = host();
  const amzTarget = `${API_VERSION}.${target}`;
  const contentType = "application/x-amz-json-1.0";

  const canonicalHeaders =
    `content-type:${contentType}\n` + `host:${h}\n` + `x-amz-date:${amzdate}\n` + `x-amz-target:${amzTarget}\n`;
  const signedHeaders = "content-type;host;x-amz-date;x-amz-target";
  const payloadHash = await sha256Hex(payload);
  const canonicalRequest = ["POST", "/", "", canonicalHeaders, signedHeaders, payloadHash].join("\n");

  if (!_accessKeyId || !_secretAccessKey) {
    throw new Error("AWS credentials are not set — fetch them from the Access API first.");
  }

  const scope = `${datestamp}/${CFG.region}/${SERVICE}/aws4_request`;
  const stringToSign = ["AWS4-HMAC-SHA256", amzdate, scope, await sha256Hex(canonicalRequest)].join("\n");

  const key = await signingKey(_secretAccessKey, datestamp, CFG.region, SERVICE);
  const signature = toHex(await hmac(key, stringToSign));

  const authorization =
    `AWS4-HMAC-SHA256 Credential=${_accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  return {
    "Content-Type": contentType,
    "X-Amz-Date": amzdate,
    "X-Amz-Target": amzTarget,
    Authorization: authorization,
  };
}

// ── Low-level call ────────────────────────────────────────────────────────────────────────

/** Fetch seam — tests stub this so the signer can be exercised without real AWS. */
type FetchImpl = (url: string, init: Record<string, any>) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;
let _fetch_impl: FetchImpl | null = null;
export function set_dynamo_fetch_impl(impl: FetchImpl | null): void {
  _fetch_impl = impl;
}
function _fetch(): FetchImpl {
  if (_fetch_impl) return _fetch_impl;
  if (typeof fetch === "undefined") throw new Error("fetch is unavailable in this environment");
  return fetch as unknown as FetchImpl;
}

// One hung request would otherwise block a slug's persist queue in session_sync FOREVER (the
// per-slug promise chain never advances and no error surfaces) — bound every call. Generous on
// purpose: a full base profile can approach DynamoDB's 400KB item cap, which a slow uplink
// needs well over 30s to push; the bound only has to catch truly hung sockets.
const _REQUEST_TIMEOUT_MS = 90_000;

async function call(target: string, body: Record<string, any>): Promise<Record<string, any>> {
  const payload = JSON.stringify(body);
  const headers = await authHeaders(target, payload);
  const res = await _fetch()(endpoint(), {
    method: "POST",
    headers,
    body: payload,
    signal:
      typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
        ? AbortSignal.timeout(_REQUEST_TIMEOUT_MS)
        : undefined,
  });
  const text = await res.text();
  if (!res.ok) {
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed.message || parsed.Message || parsed.__type || text;
    } catch {
      /* keep raw text */
    }
    throw new Error(`DynamoDB ${target} failed (${res.status}): ${message}`);
  }
  return text ? JSON.parse(text) : {};
}

// ── Public item API (payload stored as a JSON string in attribute `data`) ───────────────────

/**
 * The item shapes stored per person. `finals` (added 2026-07-05) is the marked-times comparison
 * DECISION record — the user's shortlist, their promote/demote ranking, the AI narrowing chat,
 * and (when chosen) the FINAL committed birth time — persisted as its own queryable item so the
 * settled answer stands apart from the working `base` profile.
 */
export type DynamoType = "base" | "run" | "times" | "finals";

/** Fetch one item's parsed JSON payload, or null if it does not exist. */
export async function dynamo_get(user: string, type: DynamoType): Promise<Record<string, any> | null> {
  const out = await call("GetItem", {
    TableName: CFG.table,
    Key: { user: { S: user }, type: { S: type } },
    ConsistentRead: true,
  });
  const data = out?.Item?.data?.S;
  if (typeof data !== "string") return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/** Upsert one item, storing `payload` as a JSON string under `data`. */
export async function dynamo_put(user: string, type: DynamoType, payload: Record<string, any>): Promise<void> {
  await call("PutItem", {
    TableName: CFG.table,
    Item: {
      user: { S: user },
      type: { S: type },
      data: { S: JSON.stringify(payload) },
      updated_at: { S: new Date().toISOString() },
    },
  });
}

/** All items for a user keyed by their `type` (base/run/times), parsed. */
export async function dynamo_query_user(user: string): Promise<Partial<Record<DynamoType, Record<string, any>>>> {
  const out = await call("Query", {
    TableName: CFG.table,
    KeyConditionExpression: "#u = :u",
    ExpressionAttributeNames: { "#u": "user" },
    ExpressionAttributeValues: { ":u": { S: user } },
    ConsistentRead: true,
  });
  const result: Partial<Record<DynamoType, Record<string, any>>> = {};
  for (const item of out?.Items ?? []) {
    const type = item?.type?.S as DynamoType | undefined;
    const data = item?.data?.S;
    if (!type || typeof data !== "string") continue;
    try {
      result[type] = JSON.parse(data);
    } catch {
      /* skip corrupt item */
    }
  }
  return result;
}

/**
 * Every distinct person saved in the table: their slug (partition key `user`) and display
 * `first_name` (read from each `base` item's payload). Scans the whole table filtered to the
 * `base` sort key (`type` is a DynamoDB reserved word → aliased `#t`), paging until exhausted.
 * Intended for the resume picker so any device sees everyone in the cloud, not just the people
 * this browser created.
 */
export async function dynamo_scan_base_profiles(): Promise<Array<{ slug: string; first_name: string }>> {
  const people: Array<{ slug: string; first_name: string }> = [];
  let startKey: Record<string, any> | undefined;
  let pages = 0;
  do {
    const body: Record<string, any> = {
      TableName: CFG.table,
      FilterExpression: "#t = :base",
      ExpressionAttributeNames: { "#t": "type", "#u": "user", "#d": "data" },
      ExpressionAttributeValues: { ":base": { S: "base" } },
      ProjectionExpression: "#u, #d",
    };
    if (startKey) body.ExclusiveStartKey = startKey;
    const out = await call("Scan", body);
    for (const item of out?.Items ?? []) {
      const slug = item?.user?.S;
      if (typeof slug !== "string" || !slug) continue;
      let first_name = slug;
      const data = item?.data?.S;
      if (typeof data === "string") {
        try {
          const parsed = JSON.parse(data);
          if (parsed && typeof parsed.first_name === "string" && parsed.first_name.trim()) {
            first_name = parsed.first_name;
          }
        } catch {
          /* keep the slug as the display name */
        }
      }
      people.push({ slug, first_name });
    }
    startKey = out?.LastEvaluatedKey;
  } while (startKey && ++pages < 20); // bounded; the prototype table is tiny
  return people;
}

/** Delete a single (user, type) item. Idempotent — deleting a missing item is a no-op. */
export async function dynamo_delete(user: string, type: DynamoType): Promise<void> {
  await call("DeleteItem", { TableName: CFG.table, Key: { user: { S: user }, type: { S: type } } });
}

/** Delete every known item type for a user. */
export async function dynamo_delete_user(user: string): Promise<void> {
  await Promise.all(
    (["base", "run", "times", "finals"] as DynamoType[]).map((type) =>
      call("DeleteItem", { TableName: CFG.table, Key: { user: { S: user }, type: { S: type } } }),
    ),
  );
}
